import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Pencil, Plus, Save } from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge, Button, Card, CardContent, Checkbox, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@higgins/ui';
import {
  getCatalogue, putCatalogue, type AppStatus, type CatalogueApp, type CatalogueResponse,
} from '../lib/api';
import { TileIcon } from '../components/tile-icon';
import { AppFormDialog } from '../components/app-form-dialog';

const STATUSES: { value: AppStatus; label: string }[] = [
  { value: 'live', label: 'Live' },
  { value: 'uat', label: 'UAT' },
  { value: 'dev', label: 'Dev' },
  { value: 'retired', label: 'Retired' },
];
const STATUS_ORDER: Record<AppStatus, number> = { live: 0, uat: 1, dev: 2, retired: 3 };

type Draft = Record<string, { status: AppStatus; teams: string[] }>;

/**
 * Who a status + ticks lets in, in words. Display only — the rule itself is
 * @higgins/app-access, enforced by the portal API and by every app's API.
 */
function accessSummary(status: AppStatus, tickedLabels: string[]): string {
  const teams = tickedLabels.join(', ');
  switch (status) {
    case 'dev':
      return 'Dev group only';
    case 'uat':
      return teams ? `Dev, PMO + UAT groups: ${teams}` : 'Dev, PMO';
    case 'live':
      return teams ? `Dev, PMO, IT + ${teams}` : 'Dev, PMO, IT';
    default:
      return 'Nobody';
  }
}

function toDraft(apps: CatalogueApp[]): Draft {
  return Object.fromEntries(apps.map((a) => [a.key, { status: a.status, teams: [...a.teams] }]));
}

function sameDraft(a: Draft, b: Draft): boolean {
  return Object.keys(a).every(
    (k) => a[k].status === b[k]?.status && [...a[k].teams].sort().join() === [...b[k].teams].sort().join(),
  );
}

/**
 * IT and Dev only: every app with its status and team ticks, plus Add / Edit
 * for the tile itself. /api/catalogue re-checks membership on every call, so
 * this page is convenience, not the security boundary.
 */
export default function SettingsPage({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<CatalogueResponse | null>(null);
  const [saved, setSaved] = useState<Draft>({});
  const [draft, setDraft] = useState<Draft>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{ open: boolean; editing: CatalogueApp | null }>({ open: false, editing: null });

  const load = useCallback(async () => {
    try {
      const d = await getCatalogue();
      const fresh = toDraft(d.apps);
      setData(d);
      setSaved(fresh);
      // Keep unsaved grid edits across a reload (e.g. after adding an app).
      setDraft((current) => ({ ...fresh, ...Object.fromEntries(Object.entries(current).filter(([k]) => k in fresh)) }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the app list');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(() => !sameDraft(draft, saved), [draft, saved]);
  // Grouped by SAVED status, so a row doesn't jump while you edit it.
  const rows = useMemo(
    () =>
      [...(data?.apps ?? [])].sort(
        (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.sortOrder - b.sortOrder,
      ),
    [data],
  );

  function setStatus(key: string, status: AppStatus) {
    setDraft((d) => ({ ...d, [key]: { ...d[key], status } }));
  }

  function toggleTeam(key: string, team: string) {
    setDraft((d) => {
      const teams = d[key].teams.includes(team) ? d[key].teams.filter((t) => t !== team) : [...d[key].teams, team];
      return { ...d, [key]: { ...d[key], teams } };
    });
  }

  async function save() {
    setSaving(true);
    try {
      await putCatalogue(Object.entries(draft).map(([key, v]) => ({ key, ...v })));
      toast.success('Access saved', {
        description: 'Tiles change the next time each person opens the portal; apps follow within a minute.',
      });
      await load();
    } catch (e) {
      toast.error('Could not save', { description: e instanceof Error ? e.message : 'Unknown error' });
    } finally {
      setSaving(false);
    }
  }

  /** Ticked teams' labels in column order, not tick order. */
  const tickedLabels = (ticked: string[]) =>
    (data?.teams ?? []).filter((t) => ticked.includes(t.key)).map((t) => t.label);
  const seeded = data?.seeded ?? false;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-600">
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Apps
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setForm({ open: true, editing: null })}
            disabled={!seeded}
            title={seeded ? undefined : 'Save once first to store the built-in apps'}
          >
            <Plus className="mr-1.5 h-4 w-4" /> Add app
          </Button>
          {/* Unseeded, the first Save IS the change (it stores the built-in list). */}
          <Button onClick={save} disabled={!data || (!dirty && seeded) || saving} size="sm">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Save
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-5">
          <h2 className="font-semibold text-slate-900">Apps and access</h2>
          <p className="mt-1 text-sm text-slate-600">
            Each app&apos;s <strong>status</strong> decides which group a team tick means: in UAT a Works tick
            is the Works UAT group, once Live it is the Works group. Dev sees every app at every stage, PMO
            sees UAT and Live apps, IT sees Live apps. This controls the tile <em>and</em> the app&apos;s own
            access check. Who is <em>in</em> each group is managed in Entra.
          </p>

          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
          {!data && !error && (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading apps…
            </div>
          )}

          {data && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[600px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className="py-2 pr-3 font-medium text-slate-500">App</th>
                    <th className="px-2 py-2 font-medium text-slate-500">Status</th>
                    {data.teams.map((t) => (
                      <th key={t.key} className="px-2 py-2 text-center font-medium text-slate-500">
                        {t.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((app) => {
                    const row = draft[app.key] ?? { status: app.status, teams: app.teams };
                    return (
                      <tr key={app.key} className="border-b border-slate-100 align-top">
                        <td className="py-2.5 pr-3">
                          <div className="flex items-start gap-2">
                            <TileIcon icon={app.icon} colour={app.colour} small />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1 font-medium text-slate-900">
                                {app.name}
                                {app.stagingSite && (
                                  <Badge variant="outline" className="text-[10px] font-medium">
                                    Staging
                                  </Badge>
                                )}
                                <button
                                  type="button"
                                  onClick={() => setForm({ open: true, editing: app })}
                                  disabled={!seeded}
                                  className="rounded p-0.5 text-slate-400 hover:text-slate-900 disabled:opacity-40"
                                  aria-label={`Edit ${app.name}`}
                                  title={seeded ? `Edit ${app.name}` : 'Save once first to store the built-in apps'}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              </div>
                              <p className="text-xs text-slate-500">
                                {accessSummary(row.status, tickedLabels(row.teams))}
                              </p>
                              {!app.gateLinked && (
                                <p className="text-xs text-amber-700">
                                  Tile only — this app doesn&apos;t use this check yet
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <Select value={row.status} onValueChange={(v) => setStatus(app.key, v as AppStatus)}>
                            <SelectTrigger className="h-8 w-28" aria-label={`${app.name} status`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        {data.teams.map((t) => (
                          <td key={t.key} className="px-2 py-2.5 text-center">
                            <Checkbox
                              aria-label={`${app.name} — ${t.label}`}
                              checked={row.teams.includes(t.key)}
                              onCheckedChange={() => toggleTeam(app.key, t.key)}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!seeded && (
                <p className="mt-3 text-xs text-slate-500">
                  Showing the built-in list — the first Save stores it as the live catalogue. Adding and
                  editing apps opens up after that.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <AppFormDialog
        open={form.open}
        editing={form.editing}
        onClose={() => setForm({ open: false, editing: null })}
        onSaved={() => void load()}
      />
    </div>
  );
}
